import {
  Component,
  ViewChild,
  computed,
  effect,
  inject,
  signal,
  untracked
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { SplitAreaComponent, SplitComponent } from 'angular-split';
import { Observable, catchError, combineLatest, map, of, switchMap, take } from 'rxjs';

import { AppRoutes } from '@gfe/app.paths';
import { EntityCfg } from '@gfe/core/models/entities/entity-visualization-interface';
import { JsonSchema } from '@gfe/core/models/transformers.model';
import { EntityModificationService } from '@gfe/core/services/entity-modification/entity-modification.service';
import { ICONS } from '@gfe/core/services/icons/icons.service';
import { PlanAwareNavigationService } from '@gfe/core/services/navigation/plan-aware-navigation.service';
import { NotificationService } from '@gfe/core/services/notification/notification.service';
import { UserModelsRegistryService } from '@gfe/core/services/user-models-registry/user-models-registry.service';
import { selectIsPermissionEnabled } from '@gfe/core/store/auth/auth.selectors';
import { selectIsPopupOpen } from '@gfe/core/store/data-visualization-popup/data-visualization-popup.selectors';
import {
  setVisualizedEntities,
  toggleMapVisualization
} from '@gfe/core/store/data-visualization/data-visualization.actions';
import { DataVisuComponent } from '@gfe/shared/components/data-visu/data-visu.component';
import {
  FilterDataType,
  FilterOperation,
  UidFilterCondition
} from '@gfe/shared/components/filter-query-builder/filter.models';
import { FormMode } from '@gfe/shared/components/row-edition-form/row-edition-form.utils';
import {
  ToolboxComponent,
  ToolboxSection
} from '@gfe/shared/components/toolbox/toolbox.component';

import { CodedStatusMessage } from '@core/models/coded-status-message.model';
import {
  CreateOrUpdateGlobalEntityRequest,
  EntitySelectorRequestModel,
  FilterConnector,
  PlanFilterOperator
} from '@core/models/entity/entity-selector-request.model';
import { GeoArcEditSessionService } from '@core/services/geo-arc-edit-session/geo-arc-edit-session.service';
import { GeoAreaEditSessionService } from '@core/services/geo-area-edit-session/geo-area-edit-session.service';
import { SelectorsService } from '@core/services/selectors/selectors.service';
import {
  selectAvailableEntities,
  selectAvailableTransformers,
  selectDataspaceState
} from '@core/store/dataspace/dataspace.selectors';
import { isNil } from '@core/utils/shared-utils';

import { CompatibilityEditSessionService } from '@shared/components/row-edition-form/compatibility-cfg/compatibility-edit-session.service';
import { CompatibilityRequirementPanelComponent } from '@shared/components/row-edition-form/compatibility-cfg/compatibility-requirement-panel.component';
import { FormBuilderSchema } from '@shared/components/row-edition-form/form-builder/form-builder.model';
import { FullJsonEditorComponent } from '@shared/components/row-edition-form/full-json-editor/full-json-editor.component';
import {
  FormBuilderRowResult,
  RowEditionFormComponent
} from '@shared/components/row-edition-form/row-edition-form.component';
import { FocusFirstDirective } from '@shared/directives/focus-first.directive';

import { selectEditionTransformer } from '@features/components/entities/entity/transformer-selection.utils';

type EditorMode = 'form' | 'json';

/**
 * `unsupported` is terminal: no transformer in the loaded catalog can drive a
 * form for this entity type, so nothing the page waits on will resolve it.
 */
type EntityLoadStatus = 'loading' | 'ready' | 'unsupported';

interface EntityLoadResult {
  schema: FormBuilderSchema | null;
  actualValues: Record<string, unknown>;
  status: EntityLoadStatus;
}

@Component({
  selector: 'app-entity-edit-page',
  standalone: true,
  imports: [
    RowEditionFormComponent,
    FullJsonEditorComponent,
    ToolboxComponent,
    TranslatePipe,
    SplitComponent,
    SplitAreaComponent,
    DataVisuComponent,
    CompatibilityRequirementPanelComponent,
    FocusFirstDirective
  ],
  // The zone/arc being drawn is state shared between the geometry field and the map, and
  // the compatibility requirement under edit between its card and the docked panel; this
  // page renders both ends of each. Providing the sessions here scopes each to one edit
  // page: it cannot outlive the page or be reached from anywhere else in the app. The root
  // providers remain for the maps rendered outside an edit page, where no session ever
  // begins.
  providers: [
    GeoAreaEditSessionService,
    GeoArcEditSessionService,
    CompatibilityEditSessionService
  ],
  templateUrl: './entity-edit-page.component.html'
})
export class EntityEditPageComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly store = inject(Store);
  private readonly selectorsService = inject(SelectorsService);
  private readonly entityModificationService = inject(EntityModificationService);
  private readonly translate = inject(TranslateService);
  private readonly notificationService = inject(NotificationService);
  private readonly userModelsService = inject(UserModelsRegistryService);
  private readonly navService = inject(PlanAwareNavigationService);
  private readonly geoAreaSession = inject(GeoAreaEditSessionService);
  private readonly geoArcSession = inject(GeoArcEditSessionService);
  protected readonly compatibilitySession = inject(CompatibilityEditSessionService);
  private readonly router = inject(Router);

  protected readonly ICONS = ICONS;

  private readonly routeParams = toSignal(this.route.paramMap);

  // From the route, not the plan store: the store fills in only after its own fetch, and an
  // entity loaded before that without the plan filter is not found and the page leaves.
  readonly planId = computed(
    () => this.routeParams()?.get(AppRoutes.plan.plans.view.params.planId) || null
  );
  private readonly dataspace = this.store.selectSignal(selectDataspaceState);
  private readonly allTransformers = this.store.selectSignal(selectAvailableTransformers);
  private readonly availableEntities = this.store.selectSignal(selectAvailableEntities);
  protected isPopupOpen = this.store.selectSignal(selectIsPopupOpen);

  readonly entityType = computed(
    () => this.routeParams()?.get(AppRoutes.data.view.params.entity) || ''
  );

  readonly entityUid = computed(
    () => this.routeParams()?.get(AppRoutes.data.edit.params.entityUid) || null
  );

  readonly mode = computed((): EditorMode => {
    const modeParam = this.routeParams()?.get(AppRoutes.data.new.params.mode);
    if (modeParam === 'json' || modeParam === 'form') {
      return modeParam;
    }
    return 'form';
  });

  readonly formMode = computed((): FormMode => (this.entityUid() ? 'edit' : 'create'));

  /**
   * The flavor granted for this entity type, taken from the dataspace-scoped
   * entity metadata (the same source the list view uses). Pins the form to the
   * granted flavor so a multi-flavor type (e.g. JOB) can't bind to the wrong one.
   */
  private readonly grantedVersionName = computed(() => {
    const entityType = this.entityType().toLowerCase();
    const metadata = this.availableEntities().find(
      (entity) => entity.name?.toLowerCase() === entityType
    );
    return metadata?.versions[0]?.versionName ?? null;
  });

  private readonly transformer = computed(() =>
    selectEditionTransformer(
      this.allTransformers(),
      this.entityType().toUpperCase(),
      this.grantedVersionName()
    )
  );

  private readonly entityVersionName = computed(() => {
    return this.transformer()?.transformerInfo.transformer.name || null;
  });

  private readonly entityCfg = computed<EntityCfg<unknown> | undefined>(() => {
    const versionName = this.entityVersionName();
    if (!isNil(versionName)) {
      return this.userModelsService.getEntityCfg<unknown>(versionName);
    }
    return undefined;
  });

  private readonly entityLoadResult = toSignal(
    combineLatest([
      toObservable(this.entityUid),
      toObservable(this.transformer),
      toObservable(computed(() => this.dataspace().dataspace?.id)),
      toObservable(this.allTransformers),
      toObservable(this.entityCfg),
      toObservable(this.entityType)
    ]).pipe(
      switchMap(
        ([entityUid, transformer, dataspaceId, allTransformers, entityCfg, entityType]) => {
          // The dataspace and the transformer catalog are both filled in by
          // RouteStateSyncService after startup, so their absence is transient.
          // The catalog has no loading flag of its own, hence emptiness stands in
          // for "not arrived yet".
          if (!dataspaceId || allTransformers.length === 0) {
            return of<EntityLoadResult>({
              schema: null,
              actualValues: {},
              status: 'loading'
            });
          }

          // A populated catalog that still yields no transformer never will.
          if (!transformer) {
            return of<EntityLoadResult>({
              schema: null,
              actualValues: {},
              status: 'unsupported'
            });
          }

          const versionName = transformer.transformerInfo.transformer.name;

          if (!entityUid) {
            // Create mode - synchronous result
            return of<EntityLoadResult>({
              schema: transformer.formBuilderSchema(false, (key) =>
                this.translate.instant(key)
              ),
              actualValues: {},
              status: 'ready'
            });
          }

          // Edit mode - fetch entity
          const request: EntitySelectorRequestModel = {
            entityType: entityType.toUpperCase(),
            entityTypeVersion: versionName,
            dataSpaceId: dataspaceId,
            filters: {
              rootGroup: {
                id: `edit-page-root-${Date.now()}`,
                connector: FilterConnector.AND,
                conditions: [
                  {
                    id: `uid-filter-${Date.now()}`,
                    field: 'uid',
                    dataType: FilterDataType.UID,
                    operation: FilterOperation.EQUALS,
                    value: entityUid,
                    entityType: entityType,
                    enabled: true
                  } as UidFilterCondition
                ]
              }
            }
          };

          // Add plan filter if in plan context
          const currentPlanId = untracked(() => this.planId());
          if (currentPlanId) {
            request.planFilter = {
              id: currentPlanId,
              operator: PlanFilterOperator.IN
            };
          }

          return this.selectorsService
            .selectDataParsedToEntityInterface$(request, 1, 1)
            .pipe(
              take(1),
              map((result): EntityLoadResult => {
                if (result.result.length > 0) {
                  const entity = result.result[0];
                  if (!isNil(entityCfg)) {
                    this.store.dispatch(
                      setVisualizedEntities({ entityCfg, entities: [entity] })
                    );
                  }
                  return {
                    schema: transformer.formBuilderSchema(true, (key) =>
                      this.translate.instant(key)
                    ),
                    actualValues: entity.raw as Record<string, unknown>,
                    status: 'ready'
                  };
                }
                // Entity not found - return empty result (navigation handled by effect)
                return { schema: null, actualValues: {}, status: 'ready' };
              }),
              catchError(() =>
                of<EntityLoadResult>({ schema: null, actualValues: {}, status: 'ready' })
              )
            );
        }
      )
    ),
    { initialValue: { schema: null, actualValues: {}, status: 'loading' } }
  );

  readonly isLoading = computed(() => this.entityLoadResult().status === 'loading');
  readonly isUnsupportedEntityType = computed(
    () => this.entityLoadResult().status === 'unsupported'
  );
  readonly actualValues = computed(() => this.entityLoadResult().actualValues);
  readonly schema = computed(() => this.entityLoadResult().schema);

  // Editor mode state
  protected editorMode = this.mode;

  // Toolbox and visualization state
  protected isVisuAllowed = this.store.selectSignal(selectIsPermissionEnabled('visu'));
  protected isVisuEnabled = signal<boolean>(false);
  protected isVisuVisible = computed(() => this.isVisuAllowed() && this.isVisuEnabled());
  protected currentFormValues = signal<FormBuilderRowResult | null>(null);
  protected isFormValid = signal<boolean>(false);

  // JSON Editor state
  protected jsonEditorValues = signal<Record<string, unknown>>({});
  protected isJsonEditorValid = signal<boolean>(true);

  // Raw JSON schema for JSON editor
  protected readonly rawJsonSchema = computed<JsonSchema | null>(() => {
    const transformer = this.transformer();
    if (!transformer || !transformer.transformerInfo.dataModels[0]) {
      return null;
    }
    return transformer.transformerInfo.dataModels[0].metadata.schema;
  });

  // Unified validity check for both editors
  protected readonly isCurrentEditorValid = computed(() => {
    return this.editorMode() === 'form' ? this.isFormValid() : this.isJsonEditorValid();
  });

  @ViewChild('rowEditionForm')
  rowEditionFormComponent!: RowEditionFormComponent;

  @ViewChild('fullJsonEditor')
  fullJsonEditorComponent!: FullJsonEditorComponent;

  protected toolboxSections = computed((): ToolboxSection[] => {
    const visuIcon = this.isVisuEnabled() ? ICONS.eyeSlash : ICONS.eye;
    const visuTooltip = this.translate.instant(
      this.isVisuEnabled()
        ? 'features.data.toolbox.disable_visu'
        : 'features.data.toolbox.enable_visu'
    );

    const isJsonMode = this.editorMode() === 'json';
    const editorToggleIcon = isJsonMode ? ICONS.list : ICONS.bracketsCurly;
    const editorToggleTooltip = this.translate.instant(
      isJsonMode
        ? 'features.entities.edit.switch_to_form'
        : 'features.entities.edit.switch_to_json'
    );

    const visuButton = this.isVisuAllowed()
      ? [
          {
            id: 'visu',
            icon: visuIcon,
            tooltip: visuTooltip,
            disabled: !this.isVisuEnabled() && !this.isCurrentEditorValid(),
            callback: () => this.isVisuEnabled.update((v) => !v)
          }
        ]
      : [];

    const sessionAction = this.geoArcSession.toolboxAction();
    const computeButton = sessionAction ? [sessionAction] : [];

    return [
      {
        id: 'secondary-action',
        items: [
          {
            id: 'cancel',
            tooltip: this.translate.instant('common.cancel'),
            icon: ICONS.prohibitInset,
            callback: () => this.onCancel()
          },
          {
            id: 'editor-mode',
            icon: editorToggleIcon,
            tooltip: editorToggleTooltip,
            callback: () => this.toggleEditorMode()
          },
          ...visuButton,
          ...computeButton
        ]
      },
      {
        id: 'main-action',
        items: [
          {
            id: 'submit',
            label: this.translate.instant('common.save'),
            callToAction: true,
            icon: ICONS.file,
            disabled: !this.isCurrentEditorValid(),
            callback: () => this.onSubmit()
          }
        ]
      }
    ];
  });

  constructor() {
    // Handle navigation for not-found entities
    effect(() => {
      const result = this.entityLoadResult();
      const entityUid = this.entityUid();

      if (result.status === 'ready' && entityUid && !result.schema) {
        // Entity was requested but not found
        untracked(() => {
          this.notificationService.error(
            this.translate.instant('features.entities.edit.not_found_title'),
            this.translate.instant('features.entities.edit.not_found_message', {
              uid: entityUid
            })
          );
          this.navigateToEntityList();
        });
      }
    });

    // Keep the visualization effect unchanged
    effect(() => {
      const currentValues = this.currentFormValues();
      if (!isNil(currentValues)) {
        this.updateVisu(currentValues);
      }
    });

    // A new entity has nothing to load, so nothing ever tells the store which
    // visualizations its type supports: the store keeps whatever the page the user
    // arrived from left behind, and on a direct load that is nothing at all — the map
    // the geometry field opens is then never mounted. Seeding with no entities registers
    // the capability without drawing anything; the first real value overwrites it.
    effect(() => {
      const entityCfg = this.entityCfg();
      if (isNil(entityCfg) || this.formMode() !== 'create') {
        return;
      }
      untracked(() => {
        // entityCfg is rebuilt whenever the transformer schemas reload, so the seed can
        // land after the form already has a value to show. Only the empty store needs it.
        if (!isNil(this.currentFormValues())) {
          return;
        }
        this.store.dispatch(setVisualizedEntities({ entityCfg, entities: [] }));
      });
    });

    effect(() => {
      if (this.geoAreaSession.isActive() || this.geoArcSession.isActive()) {
        untracked(() => {
          this.isVisuEnabled.set(true);
          this.store.dispatch(toggleMapVisualization({ isOpen: true }));
        });
      }
    });
  }

  private updateVisu(values: FormBuilderRowResult): void {
    const operation = this.formMode();
    const dataSpaceId = this.dataspace().dataspace?.id;
    const transformer = this.transformer()?.transformerInfo.transformer;
    const entityTypeVersion = this.entityVersionName();
    const entityCfg = this.entityCfg();

    if (
      isNil(dataSpaceId) ||
      isNil(transformer) ||
      isNil(entityTypeVersion) ||
      isNil(entityCfg)
    ) {
      return;
    }

    if (isNil(values['uid']) && operation === 'edit') {
      values['uid'] = this.entityUid();
    }

    const rawEntityData = values as Record<string, unknown>;
    const entityInterface = this.userModelsService.convertToEntityInterface<unknown>(
      [rawEntityData],
      entityTypeVersion
    );
    this.store.dispatch(
      setVisualizedEntities({
        entityCfg: entityCfg,
        entities: entityInterface,
        auxContext: { dataSpaceId }
      })
    );
  }

  onSaveRow(data: { rowId: string; values: FormBuilderRowResult }): void {
    const operation = this.formMode();
    const dataSpaceId = this.dataspace().dataspace?.id;
    const transformer = this.transformer()?.transformerInfo.transformer;
    const entityTypeVersion = this.entityVersionName();

    if (isNil(dataSpaceId) || isNil(transformer) || isNil(entityTypeVersion)) {
      return;
    }

    const values = data.values;
    if (operation === 'edit') {
      values['uid'] = data.rowId;
    }

    const request: CreateOrUpdateGlobalEntityRequest = {
      data: values as Record<string, unknown>,
      transformer: { name: transformer.name, version: transformer.version },
      dataSpaceId,
      entityTypeVersion
    };

    // Both editor modes — the dynamic form and the single-record JSON editor — save here,
    // and both write to the plan when there is one in the store.
    const save$: Observable<void | { uid?: string }> =
      operation === 'create'
        ? this.entityModificationService.planAwareCreate$(this.entityType(), request)
        : this.entityModificationService.planAwareUpdate$(this.entityType(), request);

    save$.pipe(take(1)).subscribe({
      next: (data: void | { uid?: string }) => {
        const notificationOperation =
          operation === 'edit' ? 'update_notification' : 'create_notification';
        this.notificationService.success(
          this.translate.instant(`features.entities.${notificationOperation}.title`),
          this.translate.instant(`features.entities.${notificationOperation}.message`, {
            uid: data?.uid ?? values['uid']
          })
        );
        this.navigateToEntityList();
      },
      error: (err) => {
        // The interceptor rethrows the parsed coded message; the compatibility field shows
        // the refusals in it that concern its requirements.
        this.compatibilitySession.setSaveError(err as CodedStatusMessage);
        console.error('Error saving entity:', err);
      }
    });
  }

  onCancel(): void {
    this.navigateToEntityList();
  }

  onFormValueChange(values: FormBuilderRowResult): void {
    this.currentFormValues.set(values);
  }

  onFormValidChange(isValid: boolean): void {
    this.isFormValid.set(isValid);
  }

  onJsonEditorValueChange(values: Record<string, unknown>): void {
    this.jsonEditorValues.set(values);
    // Update visualization when in JSON mode
    if (this.editorMode() === 'json' && this.isVisuEnabled()) {
      this.updateVisu(values as FormBuilderRowResult);
    }
  }

  onJsonEditorValidChange(isValid: boolean): void {
    this.isJsonEditorValid.set(isValid);
  }

  toggleEditorMode(): void {
    const currentMode = this.editorMode();
    const newMode: EditorMode = currentMode === 'form' ? 'json' : 'form';
    const dataspaceId = this.dataspace().dataspace?.id;
    const entityType = this.entityType();
    const entityUid = this.entityUid();

    if (isNil(dataspaceId)) {
      console.error('Cannot toggle editor mode: dataspace ID is undefined');
      return;
    }

    // The refusal was about the requirements as loaded; the other mode may change them.
    this.compatibilitySession.setSaveError(null);

    if (entityUid) {
      this.navService.navigateToEntityEdit(dataspaceId, entityType, entityUid, newMode);
    } else {
      this.navService.navigateToNewEntity(dataspaceId, entityType, newMode);
    }
  }

  onSubmit(): void {
    if (this.editorMode() === 'form') {
      const values = this.rowEditionFormComponent?.getCurrentValues();
      if (values) {
        this.onSaveRow({ rowId: this.entityUid() ?? '', values });
      }
    } else {
      // JSON editor mode
      if (this.isJsonEditorValid()) {
        const values = this.fullJsonEditorComponent?.getCurrentValues();
        if (values) {
          this.onSaveRow({
            rowId: this.entityUid() ?? '',
            values: values as FormBuilderRowResult
          });
        }
      }
    }
  }

  protected navigateToDataRoot(): void {
    const dataspaceId = this.dataspace().dataspace?.id;

    if (isNil(dataspaceId)) {
      console.error('Cannot navigate to data page: dataspace ID is undefined');
      return;
    }

    // Reuse the plan-aware builder, then drop the trailing entity segment: the
    // list page of an unresolvable entity type is as unusable as this form.
    const path = this.navService.buildEntityPath(dataspaceId, this.entityType());
    path.pop();
    this.router.navigate(path);
  }

  private navigateToEntityList(): void {
    const dataspaceId = this.dataspace().dataspace?.id;
    const entityType = this.entityType();

    if (isNil(dataspaceId)) {
      console.error('Cannot navigate to entity list: dataspace ID is undefined');
      return;
    }

    this.navService.navigateToEntityList(dataspaceId, entityType);
  }
}
